import { initAccordionButtons, updateAccordionFilterLabel } from './accordion';
import { dataLayerPush, getFormData, presetInputByUrlParams, setFilterInUrlParam } from '../helper/general';
import { searchDebounce } from '../helper/search-helper';
import { loadCsrfToken } from './initialize-csrf-token';

const ROOT_CLASS = 'jobs';
const ROOT_LOADING_CLASS = `${ROOT_CLASS}--loading`;
const SEARCH_INPUT_CLASS = `${ROOT_CLASS}__search-input`;
const RESULT_NUMBER_CLASS = `${ROOT_CLASS}__result-number`;
const JOB_LIST_CLASS = `${ROOT_CLASS}__list`;
const JOB_LIST_ITEM_CLASS = `${JOB_LIST_CLASS}-item`;
const JOB_ITEM_NOT_FOUND_CLASS = `${JOB_LIST_ITEM_CLASS}-not-found`;
const ALL_SELECTED_FILTER_LABEL_CLASS = `${ROOT_CLASS}__all-selected-filter-label`;

const accordionNameList = ['pensum', 'canton', 'workTimeModel'];

const hideTimeout = 80;
const SEARCH_TIMEOUT = 500;

let componentIndexCounter = 0;
const hideTimer: ReturnType<typeof setTimeout>[] = [];
const hideTimerIndex: number[] = [];

type JobList = {
  jobEntry: JobsEntry,
  jobs: JobEntry[],
};

type JobsEntry = {
  ctaTitle: string,
  ctaText: string,
}

type Place = {
  id: number,
  title: string,
  address: string,
  zip: string,
  city: string,
  canton: string,
};

type JobEntry = {
  id: number,
  title: string,
  dateCreated: string,
  dateUpdated: string,
  postDate: string,
  expiryDate: string,
  pensumMax: number,
  pensumMin: number,
  introduction: string,
  place: Place,
  workTimeMode: string,
  linkAdvertisement: string
};

export const init = (rootEl: HTMLFormElement) => {
  const searchInput = rootEl.querySelector(`.${SEARCH_INPUT_CLASS}`) as HTMLInputElement;
  const formInputList = rootEl.querySelectorAll<HTMLInputElement>(`.${SEARCH_INPUT_CLASS}`);
  const allFilterLabel = rootEl.querySelector<HTMLSpanElement>(`.${ALL_SELECTED_FILTER_LABEL_CLASS}`);

  presetInputByUrlParams(formInputList);

  rootEl.addEventListener('submit', (e) => {
    e.preventDefault();
    startSearch(rootEl);
  });

  rootEl.addEventListener('change', (e) => {
    const inputElement = e.target as HTMLInputElement;
    const isSearchInput = inputElement.classList.contains(SEARCH_INPUT_CLASS);
    if (allFilterLabel && (!isSearchInput || (inputElement.value === '' && isSearchInput))) {
      updateAccordionFilterLabel(rootEl, accordionNameList, allFilterLabel);
      startSearch(rootEl);
    }
  });

  searchInput.addEventListener('input', () => {
    const searchValue = searchInput.value;
    if (searchValue.length > 2 || searchValue.length === 0) {
      searchDebounce(SEARCH_TIMEOUT, () => {
        startSearch(rootEl);
      });
    }
  });

  if (allFilterLabel) {
    const hasUpdate = updateAccordionFilterLabel(rootEl, accordionNameList, allFilterLabel);
    if (hasUpdate) {
      startSearch(rootEl);
    }
  }
};

const setNumberResult = (rootEl: HTMLElement, count: number) => {
  const resultNumberLi = rootEl.querySelectorAll(`.${RESULT_NUMBER_CLASS}`);
  resultNumberLi.forEach((el) => {
    el.textContent = count.toString();
  });
}

const startSearch = (rootEl: HTMLFormElement) => {
  if (!rootEl) {
    return;
  }

  smoothHideOrShowJobItemList(rootEl, true);
  fetchJobs(getFormData(rootEl))
    .then((jobs) => createJobItemList(rootEl, jobs));
};

const smoothHideOrShowJobItemList = (rootEl: HTMLElement, hide: boolean = true) => {
  const listEl = rootEl.querySelectorAll<HTMLUListElement>(`.${JOB_LIST_ITEM_CLASS}`);
  if (listEl.length > 0) {
    if (hideTimer[componentIndexCounter]) {
      clearTimeout(hideTimer[componentIndexCounter]);
    }
    if (hide) {
      hideTimerIndex[componentIndexCounter] = listEl.length - 1;
    } else {
      hideTimerIndex[componentIndexCounter] = 0;
    }
    setClassCascade(listEl, hide);
  }
};

const createJobItemList = (rootEl: HTMLFormElement, jobs: JobList, pruneParams: boolean = false) => {
  rootEl.classList.remove(ROOT_LOADING_CLASS);
  const listEl = rootEl.querySelector<HTMLUListElement>(`.${JOB_LIST_CLASS}`);
  if (listEl) {
    if (hideTimer[componentIndexCounter]) {
      clearTimeout(hideTimer[componentIndexCounter]);
    }
    listEl.innerHTML = '';
    const domParser = new DOMParser();
    if (jobs.jobs.length === 0) {
      // Message when no job was found.
      setNumberResult(rootEl, 0);
      rootEl.querySelector(`.${JOB_ITEM_NOT_FOUND_CLASS}`)
        ?.classList.remove(`${JOB_ITEM_NOT_FOUND_CLASS}--hidden`);
    } else {
      setNumberResult(rootEl, jobs.jobs.length);
      jobs.jobs.forEach((job, index) => {
        const blockId = `basicsHeading-${job.id}${index}`;
        const collapseId = `${blockId}--collapse`;
        const testAttributes = index == 0 ? `data-testid="job-list-item" data-test-canton="${job.place.canton}" data-test-title="${job.title}"` : '';
        const blockContent =
          `<h5 class="accordion__header" id="${blockId}">${jobAccordionButton(collapseId, job)}</h5>` +
          `<div id="${collapseId}" class="accordion__content" aria-labelledby="${blockId}">${jobAccordionContent(job, jobs.jobEntry)}</div>`;
        const item = `<div class="accordion__block ${JOB_LIST_ITEM_CLASS} ${JOB_LIST_ITEM_CLASS}--hidden" ${testAttributes}>${blockContent}</div>`;
        const itemDom = domParser.parseFromString(item, 'text/html');
        const itemEl = itemDom.querySelector<HTMLDivElement>(`.${JOB_LIST_ITEM_CLASS}`);
        if (itemEl) {
          listEl.appendChild(itemEl);
          itemEl.addEventListener('click', () => {
            dataLayerPush('Jobs', 'Email Link', 'bewerbungs_click');
          });
        }
      });
      rootEl.querySelector(`.${JOB_ITEM_NOT_FOUND_CLASS}`)
        ?.classList.add(`${JOB_ITEM_NOT_FOUND_CLASS}--hidden`);
    }
    initAccordionButtons(listEl);
    setTimeout(() => {
      smoothHideOrShowJobItemList(rootEl, false);
    }, 1);
  }

  if (!pruneParams) {
    setFilterInUrlParam(rootEl, pruneParams);
  }
};

const jobAccordionContent = (job: JobEntry, entry: JobsEntry): string => `
  <div class="jobs__accordion-content">
    <div class="jobs__accordion-content-intro h5">
       ${job.introduction}
    </div>
    <div class="jobs__accordion-content-cta">
        <div class="jobs__cta-text">
            <h4>${entry.ctaTitle}</h4>
            <span class="jobs__cta-text">${entry.ctaText}</span>
        </div>
        <a href="${job.linkAdvertisement ?? ''}" class="button jobs__cta-button" target="_blank">${TRANSLATED_JOB_APPLY_BUTTON}</a>
    </div>
  </div>`;

const displayJobPensum = (job: JobEntry): string => {
  if (job.pensumMax === null || job.pensumMin === job.pensumMax) {
    return `${job.pensumMin}%`;
  } else {
    return `${job.pensumMin}% - ${job.pensumMax}%`;
  }
}

const jobAccordionButton = (collapseId: string, job: JobEntry): string => `
  <button
    class="accordion__button"
    type="button"
    aria-expanded="false"
    aria-controls="${collapseId}"
  >
    <div class="jobs__accordion-title accordion__button-text">
      <span class="accordion__button-text-headline">${job.title}</span>
      <span class="jobs__accordion-pensum">
        ${displayJobPensum(job)}
      </span>
      <span class="jobs__accordion-place">${job.place.canton} ${job.place.city}</span>
    </div>
    <div class="accordion__button-icon button button--icon-only button--teaser-icon"><i class="close"></i></div>
  </button>`;


const fetchJobs = async (requestBody: any): Promise<JobList> => {
  const url = `/search-jobs`;

  const csrfToken = await loadCsrfToken();

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
      },
      body: JSON.stringify(requestBody)
    });
    const jobsList = await response.json() as JobList;
    return jobsList;
  } catch (error) {
    handlingError(error);
    return {
      jobEntry: {
        ctaTitle: '',
        ctaText: '',
      },
      jobs: [],
    };
  }
};

const setClassCascade = (listEl: NodeListOf<HTMLUListElement>, hide: boolean = true) => {
  hideTimer[componentIndexCounter] = setInterval(() => {
    const index = hideTimerIndex[componentIndexCounter];
    if (listEl[index]) {
      if (hide) {
        listEl[index].classList.add(`${JOB_LIST_ITEM_CLASS}--hidden`);
      } else {
        listEl[index].classList.remove(`${JOB_LIST_ITEM_CLASS}--hidden`);
      }
    }
    if (listEl[index] && index >= 0 && index <= listEl.length - 1) {
      hideTimerIndex[componentIndexCounter] = hide ? index - 1 : index + 1;
    } else {
      clearInterval(hideTimer[componentIndexCounter]);
    }
  }, hideTimeout);
};


const handlingError = (error: any) => {
  console.error(error);
};
